import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';

import MapPlusButton from '../Cards/MapPlusButton';
import Button from '../Button';

import SiteNav from '../SiteNav';
import ProgressBar from '../ProgressBar';
import LoaderComponent from '@evdy/web-core/dist/components/shared/elements/LoaderComponent';

import { providersOperations } from '@evdy/web-redux/dist/lib/webPurchase/providers';
import { purchaseFlowOperations } from '@evdy/web-redux/dist/lib/webPurchase/purchaseFlow';
import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';

import { useScrollToTop } from '@evdy/web-core/dist/customHooks';
import { useGetWebleadFromCookie } from '../../customHooks';

import styles from './ConfirmProvider.module.scss';

const { updateWebleadData, resetWebleadUpdated } = authUserOperations;

const {
  'confirm-provider': confirmProviderClass,
  'confirm-provider-body': confirmProviderBodyClass,
  'confirm-provider-additional': additionalProvidersClass,
  'texas-policy': texasPolicyClass,
  'help-section': helpSectionClass,
  // No Providers view
  'no-providers': noProvidersClass
} = styles;

const ProvidersList = ({
  selectedProvider = {},
  handleSelectProvider,
  handleNext,
  otherProviders,
  stateWarning
}) => {
  const isTexasResident = !!stateWarning?.url;

  return (
    <div className={confirmProviderBodyClass}>
      <ProgressBar step={4} steps={4} />
      <h1>Choose a funeral home provider</h1>
      <h5>
        Don’t worry, we’ll help you switch providers if you change your mind or move somewhere else.
      </h5>
      {!!selectedProvider?._id && (
        <MapPlusButton
          isSelected
          decoratorCopy={selectedProvider.priceListingGPLUrl && 'PREFERRED'}
          title={selectedProvider.name}
          rating={selectedProvider?.ratingsInfo?.average ?? null}
          clickableRowCopy="View General Price List"
          clickableRowAction={() => {
            window.open(selectedProvider.priceListingGPLUrl, '_blank', 'noopener');
          }}
          hideClickableRow={!selectedProvider.priceListingGPLUrl}
          locationAddress={selectedProvider.address}
          buttonCopy="Select and continue"
          handleButtonClick={() => handleSelectProvider(selectedProvider?._id)}
        />
      )}
      {!!otherProviders?.length && (
        <>
          <div className={additionalProvidersClass}>
            {otherProviders.map((provider, i) => (
              <MapPlusButton
                key={`provider-${provider?.name}`}
                decoratorCopy={provider.priceListingGPLUrl && 'PREFERRED'}
                title={provider.name}
                rating={provider?.ratingsInfo?.average ?? null}
                clickableRowCopy="View General Price List"
                clickableRowAction={() => {
                  window.open(provider.priceListingGPLUrl, '_blank', 'noopener');
                }}
                hideClickableRow={!provider.priceListingGPLUrl}
                locationAddress={provider.address}
                buttonCopy="Select provider"
                handleButtonClick={() => handleSelectProvider(provider?._id)}
              />
            ))}
          </div>
        </>
      )}
      {isTexasResident && (
        <p className={texasPolicyClass}>
          As a Texas resident, by continuing you agree to state-specific policies.{' '}
          <a target="_blank" rel="noopener noreferrer" href={stateWarning.url}>
            View here.
          </a>
        </p>
      )}
      {/* <div className={helpSectionClass}>
        <span>Need help? Talk with our team.</span>
        <a href="tel:8774003297">(877) - 400-3297</a>
      </div> */}
    </div>
  );
};

const ConfirmProvider = () => {
  const dispatch = useDispatch();
  const { authUser = {}, providers = {}, purchaseFlow = {} } = useSelector(
    ({ authUser, webPurchase }) => ({
      authUser,
      providers: webPurchase?.providers,
      purchaseFlow: webPurchase?.purchaseFlow
    })
  );

  // assign most prioritized user data
  const { data: userData, isFetching: isUserFetching, webleadUpdated } = useMemo(() => {
    return !!authUser?.data?._id ? authUser : purchaseFlow;
  }, [authUser, purchaseFlow]);

  const { stateWarning, array: providersArray } = providers || {};
  const { _id: userId, address: { zip: userZipcode } = {}, eFuneral } = userData ?? {};

  // providers currently are selected right after clicking on the card's CTA button,
  // in the future we may handle selection via state before moving to next screen?
  const [selected, setSelected] = useState(0);
  const [selectedProvider, otherProviders] = useMemo(() => {
    const selectedProvider = providersArray[selected];
    const otherProviders = providersArray.filter((_, i) => i !== selected);
    return [selectedProvider, otherProviders];
  }, [selected, providersArray]);

  // scroll to top on mount
  useScrollToTop();

  // on mount, check if weblead cookie exists and fetch existing weblead
  useGetWebleadFromCookie();

  // fetch providers if zip is available
  useEffect(() => {
    if (userZipcode) {
      dispatch(providersOperations.fetchProviders(userZipcode));
    }
  }, [dispatch, userZipcode]);

  // on unmount reset providers state in redux
  useEffect(() => {
    return () => {
      dispatch(providersOperations.resetProviders());
    };
  }, []);

  // after submit, navigate to next step
  useEffect(() => {
    if (webleadUpdated) {
      dispatch(purchaseFlowOperations?.resetUpdateWeblead());
      dispatch(resetWebleadUpdated());
      browserHistory.push('/b/phone');
    }
  }, [dispatch, webleadUpdated]);

  const handleNext = async () => {
    const updatedUserBody = {
      eFuneral: {
        ...eFuneral,
        funeralHomeId: selectedProvider?._id,
        lastScreen: {
          flow: 'everdays',
          screen: 'confirmProvider',
          date: new Date().toISOString()
        }
      }
    };
    if (authUser?.data?._id) {
      dispatch(updateWebleadData(updatedUserBody));
    } else {
      dispatch(purchaseFlowOperations.updateWeblead(userId, updatedUserBody));
    }
  };

  const handleSelectProvider = async providerId => {
    const updatedUserBody = {
      eFuneral: {
        ...eFuneral,
        funeralHomeId: providerId,
        lastScreen: {
          flow: 'everdays',
          screen: 'confirmProvider',
          date: new Date().toISOString()
        }
      }
    };

    if (authUser?.data?._id) {
      dispatch(updateWebleadData(updatedUserBody));
    } else {
      dispatch(purchaseFlowOperations.updateWeblead(userId, updatedUserBody));
    }
  };

  // note: when the NoProviders section was its own component, there were issues
  // on dev and prod regarding "_ref12 is not defined". Placing it directly
  // here in the fn and not as its own component seemed to fix the issue - Marc 1.18.21
  const renderProviders = () => {
    return providers?.lastUpdated && !providersArray?.length ? (
      <div className={noProvidersClass}>
        <h2>We're working hard to get in {userZipcode || 'your area'}.</h2>
        <p>
          Everdays is not currently available in your state, but since you're early, you'll be the
          first to know as soon as we arrive.
        </p>
        <button onClick={() => browserHistory.push('/b/phone')}>Continue</button>
      </div>
    ) : (
      <ProvidersList
        {...{
          selectedProvider,
          otherProviders,
          stateWarning,
          handleSelectProvider,
          handleNext
        }}
      />
    );
  };

  const shouldRenderLoader = !!providers?.isFetching || !!isUserFetching;

  return (
    <div className={confirmProviderClass}>
      <SiteNav />
      {renderProviders()}
      {shouldRenderLoader && <LoaderComponent />}
    </div>
  );
};

export default ConfirmProvider;
